import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IImage } from '../../../domain/image.interface';
import { FormattedMessage } from 'react-intl';
import { getAssetUrl } from '../../../utils/GetAssetUrl';
import { getCroppedImg } from '../../../utils/imageUtils';
import Dropzone from 'react-dropzone';
import Cropper from 'react-easy-crop';
import './ImageUploader.component.scss';

const acceptedFileTypes = 'image/x-png, image/png, image/jpg, image/jpeg';
const containerHeight = 200;
const cropSize = {
  width: containerHeight * 0.9,
  height: containerHeight * 0.9,
};
const imageFormat = 'jpg';
const maxImageCrop = 800;

function NewImagesHolder(props: any) {
  const { images, removeImage } = props;
  return (
    <div className='image-holder-container d-flex flex-wrap justify-content-center justify-content-sm-start'>
      {images.map((image: IImage, idx: number) => (
        <div key={idx} className='fadein'>
          <div onClick={() => removeImage(idx)} className='delete-image-icon'>
            <FontAwesomeIcon icon='times-circle' />
          </div>
          <div className='preview-container'>
            <img src={image.image} alt='' />
          </div>
        </div>
      ))}
    </div>
  );
}

function OldImagesHolder(props: any) {
  const { images, removeImage } = props;
  return (
    <div className='image-holder-container d-flex flex-wrap justify-content-center justify-content-sm-start'>
      {images.map((image: any, idx: number) => (
        <div key={idx} className='fadein'>
          <div onClick={() => removeImage(idx)} className='delete-image-icon'>
            <FontAwesomeIcon icon='times-circle' />
          </div>
          <div
            className='preview-container'
            style={{
              backgroundImage: `url(${getAssetUrl.imagesStatic(
                image.url,
              )}?v=${Date.now()})`,
            }}
          ></div>
        </div>
      ))}
    </div>
  );
}

export function ImageUploaderComponent(props: any) {
  const [oldImages, setOldImages] = useState<any>([]);
  const [oldImagesToRemove, setOldImagesToRemove] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<IImage[]>([]);
  const [imageToCrop, setImageToCrop] = useState<IImage>();
  const [errorTrigger, setErrorTrigger] = useState(false);
  const [isMainPhoto, setIsMainPhoto] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const {
    name,
    maxNumImg,
    handleNewImages,
    handleOldImages,
    propsImages,
  } = props;

  const checkImagesNum =
    (!!oldImages ? oldImages.length : 0) + (!!newImages ? newImages.length : 0);

  const onCropComplete = useCallback((croppedArea, newCroppedAreaPixels) => {
    setCroppedAreaPixels(newCroppedAreaPixels);
  }, []);

  useEffect(() => {
    if (maxNumImg === 1) {
      setIsMainPhoto(true);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    submitNewImages();
    // eslint-disable-next-line
  }, [newImages]);

  useEffect(() => {
    submitNewImageInAuto();
    // eslint-disable-next-line
  }, [croppedAreaPixels]);

  useEffect(() => {
    deleteOldImages();
    // eslint-disable-next-line
  }, [oldImagesToRemove]);

  useEffect(() => {
    if (typeof propsImages === 'string') {
      setOldImages([{ url: propsImages }]);
      return;
    }
    setOldImages(propsImages);
  }, [propsImages]);

  function addImage(inputFiles: any) {
    setErrorTrigger(false);
    if (
      inputFiles.length > maxNumImg ||
      checkImagesNum + inputFiles.length > maxNumImg
    ) {
      setErrorTrigger(true);
      return;
    }
    // eslint-disable-next-line
    inputFiles.map((file: any) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result as string;
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          if (width > maxImageCrop) {
            height = (height * maxImageCrop) / width;
            width = maxImageCrop;
          }
          const elem = document.createElement('canvas');
          elem.width = width;
          elem.height = height;
          const ctx: any = elem.getContext('2d');
          // img.width and img.height will contain the original dimensions
          ctx.drawImage(img, 0, 0, width, height);
          const newImg = {
            file: file,
            image: elem.toDataURL('image/jpeg', 1) as string,
            extension: 'png',
          };
          if (isMainPhoto) {
            setImageToCrop(newImg);
          } else {
            setNewImages((prevImages: any) => [...prevImages, newImg]);
          }
        };
      };
      reader.readAsDataURL(file);
    });
  }

  const cropImage = useCallback(async () => {
    try {
      const croppedImage: any = await getCroppedImg(
        (imageToCrop as IImage).image,
        croppedAreaPixels,
        rotation,
      );
      const newImg = { image: croppedImage, extension: imageFormat };
      setNewImages([newImg]);
      setImageToCrop(undefined);
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line
  }, [croppedAreaPixels]);

  function rotateImage(deg: number) {
    setRotation(rotation + deg);
  }

  function removeNewImage(imageId: number) {
    const updatedImages = newImages.filter(
      (el: IImage, idx: number) => idx !== imageId,
    );
    setNewImages([...updatedImages]);
  }

  function removeOldImage(imageId: number) {
    const updatedImages = oldImages.filter(
      (el: any, idx: number) => idx !== imageId,
    );
    setOldImages([...updatedImages]);
    if (!!oldImages[imageId].id) {
      setOldImagesToRemove((prevIds: string[]) => [
        ...prevIds,
        oldImages[imageId].id,
      ]);
    }
  }

  function submitNewImages() {
    if (!newImages.length) {
      return;
    }
    if (newImages.length === 1 && isMainPhoto) {
      handleNewImages(...newImages, name);
      return;
    }
    const body = new FormData();
    newImages.forEach((image: IImage) =>
      body.append('image', image.file as File),
    );
    handleNewImages(body, name);
  }

  async function submitNewImageInAuto() {
    if (!imageToCrop) {
      return;
    }
    if (!!croppedAreaPixels) {
      try {
        const croppedImage: any = await getCroppedImg(
          (imageToCrop as IImage).image,
          croppedAreaPixels,
          rotation,
        );
        handleNewImages({ image: croppedImage, extension: imageFormat }, name);
      } catch (e) {
        console.error(e);
      }
    } else {
      handleNewImages({ image: imageToCrop.image, extension: imageFormat }, name);
    }
  }

  function deleteOldImages() {
    return handleOldImages ? handleOldImages(oldImagesToRemove) : null;
  }

  return (
    <div className='image-uploader-container'>
      {checkImagesNum < maxNumImg && !imageToCrop && (
        <div className='dropzone-container field-container'>
          <Dropzone
            data-cy='drag-and-drop'
            accept={acceptedFileTypes}
            onDrop={(inputFiles: any) => addImage(inputFiles)}
          >
            {({ getRootProps, getInputProps }) => (
              <div className='dropzone'>
                <div {...getRootProps()}>
                  <label htmlFor={name}>
                    <FontAwesomeIcon icon='images' size='3x' />
                    <FontAwesomeIcon icon='plus' />
                  </label>
                  <input {...getInputProps({ name: 'image' })} />
                </div>
                {errorTrigger ? (
                  <p className='image-error-container align-self-center'>
                    <FormattedMessage
                      id='organism.imageUploader.error'
                      values={{ value: maxNumImg }}
                    />
                  </p>
                ) : (
                  <p className='text-primary-lg font-weight-light align-self-center'>
                    <FormattedMessage
                      id='organism.imageUploader.paragraph'
                      values={{ value: maxNumImg }}
                    />
                  </p>
                )}
              </div>
            )}
          </Dropzone>
        </div>
      )}
      {checkImagesNum < maxNumImg && !!imageToCrop && (
        <div className='crop-container d-flex flex-wrap justify-content-center justify-content-sm-start'>
          <div className='controls-container'>
            <div
              className='control'
              onClick={(e: any) => {
                e.preventDefault();
                cropImage();
              }}
            >
              <FontAwesomeIcon icon='check' />
            </div>
            <div
              className='control'
              onClick={(e: any) => {
                e.preventDefault();
                rotateImage(-90);
              }}
            >
              <FontAwesomeIcon icon='undo-alt' />
            </div>
            <div
              className='control'
              onClick={(e: any) => {
                e.preventDefault();
                rotateImage(90);
              }}
            >
              <FontAwesomeIcon icon='redo-alt' />
            </div>
            <div
              className='control'
              onClick={(e: any) => {
                e.preventDefault();
                setImageToCrop(undefined);
              }}
            >
              <FontAwesomeIcon icon='times' />
            </div>
          </div>
          <Cropper
            image={imageToCrop ? imageToCrop.image : undefined}
            aspect={1 / 1}
            cropShape='round'
            cropSize={cropSize}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            onMediaLoaded={mediaSize => {
              setZoom(containerHeight / mediaSize.height);
            }}
          />
        </div>
      )}
      {!!newImages && !!newImages.length && (
        <NewImagesHolder images={newImages} removeImage={removeNewImage} />
      )}
      {!!oldImages && !!oldImages.length && (
        <OldImagesHolder images={oldImages} removeImage={removeOldImage} />
      )}
    </div>
  );
}
